// Helper functions for workflow validation

export interface ValidationError {
  path?: string[];
  message: string;
  code?: string;
}

/**
 * Formats Zod validation errors into readable messages
 */
export function formatZodErrors(errors: any[]): string[] {
  return errors.map(error => {
    if (error.path && error.path.length > 0) {
      const path = error.path.join('.');
      
      // Special handling for activityType errors
      if (path.includes('activityType')) {
        const nodeMatch = path.match(/nodes\.(\d+)\.activityType/);
        if (nodeMatch) {
          const nodeIndex = parseInt(nodeMatch[1]);
          return `🔧 Activity Node #${nodeIndex + 1}: Click the node to set its activity type (Approval, Assignment, etc.)`;
        }
      }
      
      let message = `${path}: `;
      
      switch (error.code) {
        case 'invalid_type':
          message += `Expected ${error.expected}, got ${error.received}`;
          break;
        case 'invalid_enum_value':
          if (path.includes('activityType')) {
            message = `🔧 Activity configuration needed: Click the node and select its type`;
          } else {
            message += `Invalid value. Expected one of: ${error.options?.join(', ')}`;
          }
          break;
        case 'too_small':
          if (error.type === 'string') {
            message += `Must be at least ${error.minimum} characters`;
          } else if (error.type === 'array') {
            message += `Must have at least ${error.minimum} items`;
          } else {
            message += `Value too small`;
          }
          break;
        case 'too_big':
          if (error.type === 'string') {
            message += `Must be at most ${error.maximum} characters`;
          } else if (error.type === 'array') {
            message += `Must have at most ${error.maximum} items`;
          } else {
            message += `Value too large`;
          }
          break;
        default:
          message += error.message;
      }
      
      return message;
    }
    return error.message || 'Validation error';
  });
}

/**
 * Gets a user-friendly label for a node
 */
function getNodeLabel(node: any): string {
  if (node.data?.label) {
    return node.data.label;
  }
  
  // Generate friendly label based on node type
  switch (node.type) {
    case 'START':
      return 'Start';
    case 'END':
      return 'End';
    case 'ACTIVITY':
      return node.data?.activityType ? 
        node.data.activityType.charAt(0) + node.data.activityType.slice(1).toLowerCase().replace(/_/g, ' ') : 
        'Activity';
    case 'CONDITION':
      return 'Decision';
    case 'PARALLEL_SPLIT':
      return 'Parallel Split';
    case 'PARALLEL_JOIN':
      return 'Parallel Join';
    default:
      // If it's an auto-generated ID, make it more friendly
      if (node.id.includes('-')) {
        const parts = node.id.split('-');
        return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      }
      return node.id;
  }
}

/**
 * Validates workflow structure on the client side
 */
export function validateWorkflowStructure(nodes: any[], edges: any[]): string[] {
  const errors: string[] = [];
  
  // Check for start nodes
  const startNodes = nodes.filter(n => n.type === 'START');
  if (startNodes.length === 0) {
    errors.push('🚫 Missing Start Node: Add a Start node from the Flow Control section');
  } else if (startNodes.length > 1) {
    errors.push('⚠️ Multiple Start Nodes: A workflow can only have one Start node. Please remove the extra Start nodes');
  }
  
  // Check for end nodes
  const endNodes = nodes.filter(n => n.type === 'END');
  if (endNodes.length === 0) {
    errors.push('🚫 Missing End Node: Add an End node from the Flow Control section');
  }
  
  // Check if all non-terminal nodes are connected
  const connectedNodeIds = new Set<string>();
  const nodeIncomingEdges = new Map<string, number>();
  const nodeOutgoingEdges = new Map<string, number>();
  
  // Count incoming and outgoing edges for each node
  edges.forEach(edge => {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
    
    nodeOutgoingEdges.set(edge.source, (nodeOutgoingEdges.get(edge.source) || 0) + 1);
    nodeIncomingEdges.set(edge.target, (nodeIncomingEdges.get(edge.target) || 0) + 1);
  });
  
  // Check each node for connectivity issues
  nodes.forEach(node => {
    const label = getNodeLabel(node);
    const incoming = nodeIncomingEdges.get(node.id) || 0;
    const outgoing = nodeOutgoingEdges.get(node.id) || 0;
    
    if (node.type === 'START') {
      if (outgoing === 0) {
        errors.push(`🔗 "${label}" needs connection: Draw a line from the Start node to your first activity`);
      }
    } else if (node.type === 'END') {
      if (incoming === 0) {
        errors.push(`🔗 "${label}" needs connection: Draw a line from your last activity to the End node`);
      }
    } else {
      // Activity nodes need both incoming and outgoing
      if (incoming === 0 && outgoing === 0) {
        errors.push(`🔗 "${label}" is isolated: Connect this node by dragging from/to the connection points`);
      } else if (incoming === 0) {
        errors.push(`🔗 "${label}" has no input: Draw a line from a previous step to this node`);
      } else if (outgoing === 0) {
        errors.push(`🔗 "${label}" has no output: Draw a line from this node to the next step or End node`);
      }
    }
    
    // Special checks for specific node types
    if (node.type === 'CONDITION') {
      if (outgoing === 1) {
        errors.push(`⚠️ "${label}" decision node: Add both Yes and No branches (currently has only one output)`);
      }
    }
    
    if (node.type === 'PARALLEL_SPLIT' && outgoing < 2) {
      errors.push(`⚠️ "${label}" split node: Add at least 2 parallel branches`);
    }
    
    if (node.type === 'PARALLEL_JOIN' && incoming < 2) {
      errors.push(`⚠️ "${label}" join node: Connect at least 2 incoming branches`);
    }
  });
  
  // Check for orphaned edges (shouldn't happen but good to check)
  const nodeIds = new Set(nodes.map(n => n.id));
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  
  edges.forEach((edge, _index) => {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      const sourceLabel = nodeMap.get(edge.source) ? getNodeLabel(nodeMap.get(edge.source)) : 'deleted node';
      const targetLabel = nodeMap.get(edge.target) ? getNodeLabel(nodeMap.get(edge.target)) : 'deleted node';
      errors.push(`❌ Broken connection: Line from "${sourceLabel}" to "${targetLabel}" references a deleted node`);
    }
  });
  
  return errors;
}